import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api-client.js';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { FormBanner } from '../components/ui/form.js';

type CampaignStatus = 'pending' | 'in_progress' | 'completed' | 'failed';
type CampaignChannel = 'auto_dialer' | 'ai_message' | 'sms' | 'email';
type ChannelStatus = 'na' | 'pending' | 'sent' | 'completed' | 'failed' | 'caller_disconnect';

interface Campaign {
  id: string;
  tag: string;
  contact_profile_id?: string | null;
  message_template_id?: string | null;
  recipient_name?: string;
  recipient_email?: string;
  recipient_phone: string;
  start_at: string;
  channels: CampaignChannel[];
  status: CampaignStatus;
  delivery_status: Partial<Record<CampaignChannel, ChannelStatus>>;
  parent_campaign_id?: string | null;
  dnc: boolean;
  created_at: string;
  updated_at: string;
}

const STATUS_TONE: Record<CampaignStatus, 'neutral' | 'info' | 'success' | 'danger'> = {
  pending: 'neutral',
  in_progress: 'info',
  completed: 'success',
  failed: 'danger',
};

const DELIVERY_TONE: Record<ChannelStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  na: 'neutral',
  pending: 'neutral',
  sent: 'info',
  completed: 'success',
  failed: 'danger',
  caller_disconnect: 'warning',
};

const CHANNEL_LABEL: Record<CampaignChannel, string> = {
  auto_dialer: 'auto-dialer',
  ai_message: 'AI message',
  sms: 'SMS',
  email: 'email',
};

export function CampaignDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const campaignQ = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => api.get(`/v1/campaigns/${id}`) as Promise<Campaign>,
    enabled: !!id,
  });

  const cancel = useMutation({
    mutationFn: () => api.delete(`/v1/campaigns/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      navigate('/campaigns');
    },
  });

  if (campaignQ.isLoading) return <p className="text-sm text-ink-muted">Loading…</p>;
  if (campaignQ.error) return <FormBanner message={(campaignQ.error as Error).message} />;
  if (!campaignQ.data) return <FormBanner message="Campaign not found" />;
  const c = campaignQ.data;
  const editable = c.status === 'pending';

  return (
    <div className="max-w-3xl space-y-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <Link to="/campaigns" className="text-sm text-ink-muted hover:text-ink">← Campaigns</Link>
          <h1 className="text-2xl font-semibold mt-1">{c.tag}</h1>
          <div className="flex items-center gap-2 mt-1 text-sm">
            <Badge tone={STATUS_TONE[c.status]}>{c.status.replace('_', ' ')}</Badge>
            {c.dnc && <Badge tone="warning">DNC suppressed</Badge>}
          </div>
        </div>
        <div className="flex gap-2">
          {editable && (
            <Link to={`/campaigns/${c.id}/edit`}>
              <Button variant="secondary">Edit</Button>
            </Link>
          )}
          <Button
            variant="ghost"
            onClick={() => {
              if (window.confirm(`Cancel campaign "${c.tag}"?`)) cancel.mutate();
            }}
            disabled={cancel.isPending}
          >
            Cancel campaign
          </Button>
        </div>
      </header>

      <Card>
        <CardHeader><CardTitle>When</CardTitle></CardHeader>
        <CardBody>
          <DefList items={[['Start', new Date(c.start_at).toLocaleString()]]} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader><CardTitle>Recipient</CardTitle></CardHeader>
        <CardBody>
          <DefList
            items={[
              ['Name', c.recipient_name ?? '—'],
              [
                'Phone',
                <a className="text-accent hover:underline" href={`tel:${c.recipient_phone}`}>
                  <code>{c.recipient_phone}</code>
                </a>,
              ],
              [
                'Email',
                c.recipient_email ? (
                  <a className="text-accent hover:underline" href={`mailto:${c.recipient_email}`}>
                    {c.recipient_email}
                  </a>
                ) : '—',
              ],
            ]}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader><CardTitle>Channels</CardTitle></CardHeader>
        <CardBody className="p-0">
          <ul className="divide-y divide-border">
            {c.channels.map((ch) => {
              const status = c.delivery_status[ch];
              return (
                <li key={ch} className="flex items-center justify-between px-4 py-3 text-sm">
                  <span className="font-medium">{CHANNEL_LABEL[ch]}</span>
                  {status ? (
                    <Badge tone={DELIVERY_TONE[status]}>{status.replace('_', ' ')}</Badge>
                  ) : (
                    <span className="text-ink-muted text-xs">queued</span>
                  )}
                </li>
              );
            })}
          </ul>
        </CardBody>
      </Card>

      <p className="text-xs text-ink-muted text-right">
        Created {new Date(c.created_at).toLocaleString()} · updated {new Date(c.updated_at).toLocaleString()}
      </p>
    </div>
  );
}

function DefList({ items }: { items: [string, React.ReactNode][] }): JSX.Element {
  return (
    <dl className="grid grid-cols-[140px_1fr] gap-y-2 text-sm">
      {items.map(([k, v], i) => (
        <span key={i} className="contents">
          <dt className="text-ink-muted">{k}</dt>
          <dd>{v}</dd>
        </span>
      ))}
    </dl>
  );
}
